import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { AlertTriangleIcon } from "lucide-react";
import { TRIAL_GRACE_DAYS } from "@constant/subscription";

type TrialWarningType = "ending_soon" | "usage_limit" | "usage_limit_ending_soon";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onViewPricingPlans: () => void;
    type: TrialWarningType;
    /** Trial days remaining — used by `ending_soon` and `usage_limit_ending_soon` variants. */
    daysRemaining?: number;
    /** True for training-agent workspaces → training-worded copy (#2687). */
    isTraining?: boolean;
}

const SUPPORT_EMAIL = "support@ourteam.ai";

function SupportEmailLink() {
    return (
        <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-blue-600 hover:underline"
        >
            {SUPPORT_EMAIL}
        </a>
    );
}

function pluralize(n: number, word: string) {
    return `${n} ${word}${n === 1 ? "" : "s"}`;
}

const ICON_BG = "bg-amber-100";
const ICON_COLOR = "text-amber-500";

export default function TrialWarningDialog({
    isOpen,
    onClose,
    onViewPricingPlans,
    type,
    daysRemaining,
    isTraining = false,
}: Props) {
    const safeDays = typeof daysRemaining === "number" && Number.isFinite(daysRemaining)
        ? Math.max(0, daysRemaining)
        : 0;

    // When safeDays === 0 the trial's last day is "today" — the workspace
    // actually ends tomorrow morning. Rendering "expires in 0 days" reads
    // wrong, so we substitute the entire verb phrase to match the
    // TrialUsageBanner "Ends tomorrow" wording.
    const trialEndPhrase = safeDays === 0
        ? "ends tomorrow"
        : `expires in ${pluralize(safeDays, "day")}`;

    const title =
        type === "ending_soon"
            ? "Trial Workspace Ending Soon"
            : "Trial Usage Limit Reached";

    // Training-agent workspaces get training-worded copy (#2687); recruiter (and
    // any other) workspaces keep the existing recruiter wording.
    const activityNoun = isTraining ? "training activity" : "hiring activity";
    const itemsAndPeople = isTraining
        ? "campaigns and trainees"
        : "jobs and candidates";
    const continueVerb = isTraining ? "training" : "hiring";
    const pausedNoun = isTraining ? "campaigns" : "jobs";

    /**
     * What actually happens at expiry, read from the constant that decides it.
     *
     * This sentence used to promise "your existing jobs will remain active for
     * 7 more days before the workspace enters read-only mode" — hardcoded, and
     * false since TRIAL_GRACE_DAYS became 0. The workspace is restricted the
     * day the trial ends; there is no week.
     *
     * subscription.ts calls itself "the SINGLE source of truth ... so the
     * 'N days left' copy can never disagree with when actions actually block"
     * (#2689). The banner reads it. This dialog never did, which is how the two
     * came to describe different products. Reading it here means changing the
     * window changes the promise, in both places, in one edit.
     */
    const whatHappensAtExpiry = TRIAL_GRACE_DAYS > 0
        ? `Once it expires, your existing ${pausedNoun} stay active for ${pluralize(TRIAL_GRACE_DAYS, "more day")} before the workspace becomes read-only.`
        : `Once it expires, your ${pausedNoun} are paused and the workspace becomes read-only straight away.`;

    let body: React.ReactNode = null;

    if (type === "ending_soon") {
        body = (
            <>
                <p>
                    Your trial workspace <strong>{trialEndPhrase}</strong>. {whatHappensAtExpiry}<br /><br />Upgrade now to keep all your {activityNoun} running without interruption.
                </p>
                <p>
                    To continue, view our pricing plans or contact <SupportEmailLink />.
                </p>
            </>
        );
    } else if (type === "usage_limit") {
        body = (
            <>
                <p>
                    You've reached the limit for your trial workspace. <br /> Your existing {itemsAndPeople} remain active.
                </p>
                <p>
                    To continue {continueVerb}, view our pricing plans or contact <SupportEmailLink />.
                </p>
            </>
        );
    } else {
        // usage_limit_ending_soon
        body = (
            <>
                <p>
                    You've reached the limit for your trial workspace, and your trial <strong>{trialEndPhrase}</strong>.<br />Your existing {itemsAndPeople} remain active for now.
                </p>
                <p>
                    To continue {continueVerb} without interruption, view our pricing plans or contact <SupportEmailLink />.
                </p>
            </>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader className="space-y-4">
                    <div className="flex items-center gap-3">
                        <div className={`flex items-center justify-center w-10 h-10 rounded-full ${ICON_BG}`}>
                            <AlertTriangleIcon className={`h-5 w-5 ${ICON_COLOR}`} />
                        </div>
                        <DialogTitle className="text-xl font-semibold">
                            {title}
                        </DialogTitle>
                    </div>
                    <DialogDescription className="text-base text-gray-600 space-y-3">
                        {body}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="flex gap-3 sm:gap-3 mt-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                    >
                        Close
                    </Button>
                    <Button
                        type="button"
                        onClick={onViewPricingPlans}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        Get in Touch
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
